import {
  fromPixel,
  type LonLatInput,
  toPixel as toFractionalPixel
} from '@conveyal/lonlat'
import {area, bboxPolygon, distance} from '@turf/turf'

// For web mercator <> wgs bounds conversions.
const WGS_EPSILON = 0.00001

/**
 *
 */
export class WebMercatorExtents implements CL.WebMercatorExtents {
  north: number
  west: number
  width: number
  height: number
  zoom: number

  constructor({north, west, width, height, zoom}: CL.WebMercatorExtents) {
    this.north = north
    this.west = west
    this.width = width
    this.height = height
    this.zoom = zoom
  }

  areaKm(): number {
    return area(this.toBboxPolygon()) / 1_000_000
  }

  cellWidthM(): number {
    const nw = fromPixel({x: this.west, y: this.north}, this.zoom)
    const edge = fromPixel({x: this.west + 1, y: this.north}, this.zoom)
    return distance([nw.lon, nw.lat], [edge.lon, edge.lat], {
      units: 'meters'
    })
  }

  toBbox(): [number, number, number, number] {
    const bounds = WebMercatorExtents.toWgsBounds(this)
    return [bounds.west, bounds.south, bounds.east, bounds.north]
  }

  toBboxPolygon() {
    return bboxPolygon(this.toBbox())
  }

  totalPoints(): number {
    return this.width * this.height
  }

  /**
   * Construct a `WebMercatorExtents` from a supplied WGS84 bounds.
   *
   * This behavior mirrors R5's com.conveyal.r5.analyst.WebMercatorExtents class.
   *
   * NOTE: This should only be used for display purposes.
   */
  static fromWgsBounds(wgsBounds: CL.WgsBounds, zoom: number) {
    const nw = pixelCellContainingLonLat(
      {lon: wgsBounds.west, lat: wgsBounds.north},
      zoom
    )
    const se = toFractionalPixel(
      {lon: wgsBounds.east, lat: wgsBounds.south},
      zoom
    )
    return new WebMercatorExtents({
      north: nw.y,
      west: nw.x,
      width: Math.ceil(se.x) - nw.x,
      height: Math.ceil(se.y) - nw.y,
      zoom
    })
  }

  /**
   * Wrapper to `fromWgsBounds` where the bounds is trimmed uniformly by only a meter or two in
   * each direction.
   *
   * This behavior mirrors R5's com.conveyal.r5.analyst.WebMercatorExtents class.
   */
  static fromTrimmedWgsBounds(
    {north, south, east, west}: CL.WgsBounds,
    zoom: number
  ) {
    const trimmedBounds: CL.WgsBounds = {
      north: north - WGS_EPSILON,
      south: south + WGS_EPSILON,
      east: east - WGS_EPSILON,
      west: west + WGS_EPSILON
    }
    return WebMercatorExtents.fromWgsBounds(trimmedBounds, zoom)
  }

  /**
   * Convert web mercator extents to WGS bounds.
   *
   * This behavior mirrors R5's com.conveyal.r5.analyst.WebMercatorExtents class.
   */
  static toWgsBounds(webMercatorExtents: CL.WebMercatorExtents): CL.WgsBounds {
    const nw = fromPixel(
      {
        x: webMercatorExtents.west,
        y: webMercatorExtents.north
      },
      webMercatorExtents.zoom
    )
    const se = fromPixel(
      {
        x: webMercatorExtents.west + webMercatorExtents.width,
        y: webMercatorExtents.north + webMercatorExtents.height
      },
      webMercatorExtents.zoom
    )
    return {
      east: se.lon,
      north: nw.lat,
      south: se.lat,
      west: nw.lon
    }
  }
}

/**
 * Convert a lon, lat coordinate and zoom level to a Web Mercator pixel. By using truncation the
 * returned pixel represents the top left corner of the cell.
 */
export function pixelCellContainingLonLat(lonlat: LonLatInput, zoom: number) {
  const pixel = toFractionalPixel(lonlat, zoom)
  // Truncate them to get top left of cell the point is a part of.
  return {
    x: pixel.x | 0,
    y: pixel.y | 0
  }
}

/**
 * Find the nearest {x, y} pixel to the geographic coordinates.
 *
 * If the input is closer to the edge of the next pixel, this will return that pixel coordinate.
 */
export function findNearestPixelToLonLat(lonlat: LonLatInput, zoom: number) {
  const {x, y} = toFractionalPixel(lonlat, zoom)
  return {
    x: Math.round(x),
    y: Math.round(y)
  }
}

/**
 * Snap a lon/lat coordinate to the nearest grid point by projecting and
 * rounding the {x, y} values to the nearest pixel and then converting them back to coordinates.
 */
export function snapCoordinatesToNearestPixel(
  lonlat: LonLatInput,
  zoom: number
) {
  return fromPixel(findNearestPixelToLonLat(lonlat, zoom), zoom)
}

/**
 * Snap lon/lat bounds to their closest pixel coordinates.
 */
export function snapBoundsToNearestPixels(
  bounds: CL.WgsBounds,
  zoom: number
): CL.WgsBounds {
  const nw = snapCoordinatesToNearestPixel(
    {lat: bounds.north, lon: bounds.west},
    zoom
  )
  const se = snapCoordinatesToNearestPixel(
    {lat: bounds.south, lon: bounds.east},
    zoom
  )
  return {north: nw.lat, west: nw.lon, south: se.lat, east: se.lon}
}

/**
 * Get the web mercator pixel cell bounds as geographic coordinates for a given lon / lat input.
 */
export function pixelCellBoundsForLonLat(
  lonlat: LonLatInput,
  zoom: number
): CL.WgsBounds {
  const p0 = pixelCellContainingLonLat(lonlat, zoom)
  const p1 = {x: p0.x + 1, y: p0.y + 1}
  const nw = fromPixel(p0, zoom)
  const se = fromPixel(p1, zoom)
  return {north: nw.lat, west: nw.lon, south: se.lat, east: se.lon}
}
